import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  features: string[];
}

const services: Service[] = [
{
  id: 1,
  title: 'Market Entry Strategy',
  description: 'Comprehensive regulatory roadmap for entering new pharmaceutical markets',
  icon: 'GlobeAltIcon',
  features: ['Regulatory Assessment', 'Documentation Planning', 'Timeline Optimization', 'Cost Analysis']
},
{
  id: 2,
  title: 'Compliance Auditing',
  description: 'Thorough evaluation of manufacturing and quality systems against global standards',
  icon: 'ClipboardDocumentCheckIcon',
  features: ['GMP Audits', 'Gap Analysis', 'Corrective Actions', 'Audit Preparation']
},
{
  id: 3,
  title: 'Documentation Services',
  description: 'Expert preparation of regulatory dossiers and submission documents',
  icon: 'DocumentTextIcon',
  features: ['CTD Preparation', 'Product Dossiers', 'Technical Files', 'Variation Support']
},
{
  id: 4,
  title: 'Regulatory Intelligence',
  description: 'Stay ahead with real-time updates on regulatory changes and requirements',
  icon: 'LightBulbIcon',
  features: ['Regulatory Monitoring', 'Policy Updates', 'Market Intelligence', 'Strategic Guidance']
}];


export default function RegulatoryExpertise() {
  return (
    <section className="py-12 bg-gradient-to-b from-muted/30 to-background">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-secondary/10 rounded-full mb-3">
            <Icon name="DocumentCheckIcon" size={18} className="text-secondary" />
            <span className="text-xs font-medium text-secondary">Regulatory Excellence</span>
          </div>
          <h2 className="font-heading font-bold text-2xl lg:text-3xl text-textPrimary mb-3">
            Regulatory Consultancy Services
          </h2>
          <p className="text-sm text-textSecondary max-w-2xl mx-auto">
            Navigate complex regulatory landscapes with confidence. Our expert team provides comprehensive consultancy services to ensure compliance and accelerate market access.
          </p>
        </div>

        {/* Services Grid - Single Line */}
        <div className="overflow-x-auto pb-4 mb-8">
          <div className="flex gap-4 min-w-max">
            {services.map((service) =>
            <div
              key={service.id}
              className="bg-card rounded-xl p-5 shadow-md hover:shadow-xl transition-all duration-300 border border-border w-72 flex-shrink-0">

                {/* Icon & Title */}
                <div className="flex items-start space-x-3 mb-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-secondary/10 rounded-xl flex-shrink-0">
                    <Icon name={service.icon as any} size={24} className="text-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-base text-textPrimary mb-1.5 break-words">{service.title}</h3>
                    <p className="text-xs text-textSecondary line-clamp-2 break-words">{service.description}</p>
                  </div>
                </div>

                {/* Features List */}
                <div className="space-y-2">
                  {service.features.map((feature, index) =>
                <div key={index} className="flex items-center space-x-2">
                      <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0" variant="solid" />
                      <span className="text-xs text-textPrimary break-words">{feature}</span>
                    </div>
                )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Key Benefits Section */}
        <div className="bg-trustBuilder rounded-2xl p-6 lg:p-8 text-trustBuilder-foreground">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-heading font-bold text-xl lg:text-2xl mb-3 break-words">
                Why Choose Our Regulatory Services?
              </h3>
              <p className="text-sm text-trustBuilder-foreground/80 mb-4 break-words">With over 5 years of experience and successful regulatory approvals across 6+ countries, we bring unmatched expertise to your pharmaceutical compliance needs.

              </p>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Icon name="ShieldCheckIcon" size={20} className="text-trustBuilder-foreground flex-shrink-0" variant="solid" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm mb-1 break-words">Proven Track Record</h4>
                    <p className="text-xs text-trustBuilder-foreground/70 break-words">
                      Successfully guided 500+ products through regulatory approval processes
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="UserGroupIcon" size={20} className="text-trustBuilder-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm mb-1 break-words">Expert Team</h4>
                    <p className="text-xs text-trustBuilder-foreground/70 break-words">
                      Dedicated regulatory professionals with international experience
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="ClockIcon" size={20} className="text-trustBuilder-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm mb-1 break-words">Faster Approvals</h4>
                    <p className="text-xs text-trustBuilder-foreground/70 break-words">
                      Streamlined processes reducing time-to-market by up to 40%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-trustBuilder-foreground/10 rounded-xl p-6">
              <h4 className="font-heading font-semibold text-lg mb-4 break-words">Request a Consultation</h4>
              <p className="text-xs text-trustBuilder-foreground/80 mb-4 break-words">
                Get expert guidance tailored to your specific regulatory challenges. Our team is ready to help you navigate complex compliance requirements.
              </p>
              <Link
                href="/regulatory-consultancy-services"
                className="inline-flex items-center px-5 py-2.5 bg-cta text-cta-foreground font-cta font-semibold text-sm rounded-md hover:-translate-y-1 hover:shadow-cta transition-all duration-300 w-full justify-center">

                Explore Services
                <Icon name="ArrowRightIcon" size={18} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>);
}